import { Badge, Box, Flex, ScrollArea, Text } from '@radix-ui/themes';
import {
  Battery,
  BatteryCharging,
  Cable,
  Clock,
  Cpu,
  HardDrive,
  Lock,
  MemoryStick,
  Sun,
  Wifi,
} from 'lucide-react';
import type { CodexUsage, MacMetrics, NetLink } from '@mac-remote/protocol';
import { CodexLimits } from '../components/CodexLimits';

const GB = 1024 ** 3;

function gb(bytes: number): string {
  return `${(bytes / GB).toFixed(1)} ГБ`;
}

function duration(totalSec: number): string {
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${d} дн ${h} ч`;
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
}

function minutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
}

/** pmset's vocabulary, in Russian. Anything unrecognised is passed through rather
 *  than swallowed — a state we have not seen is still information. */
const BATTERY_STATE: Record<string, string> = {
  charging: 'заряжается',
  charged: 'заряжена',
  discharging: 'разряжается',
  'finishing charge': 'дозаряжается',
  'AC attached': 'от сети',
};

/**
 * Signal quality bands for 2.4/5 GHz Wi-Fi. Above -67 dBm is what Apple itself
 * treats as good enough for video; below -80 dBm barely holds a connection.
 */
function signalLabel(dbm: number): { text: string; color: 'jade' | 'amber' | 'red' } {
  if (dbm >= -67) return { text: 'хороший', color: 'jade' };
  if (dbm >= -80) return { text: 'слабый', color: 'amber' };
  return { text: 'плохой', color: 'red' };
}

function Row({
  icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  accent?: 'jade' | 'amber' | 'red';
}) {
  return (
    <Flex align="start" gap="3" style={{ padding: '10px 12px' }}>
      <Box style={{ color: 'var(--app-hint)', flex: '0 0 auto', paddingTop: 1 }}>{icon}</Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="2" style={{ color: 'var(--app-hint)' }}>
          {label}
        </Text>
        {hint && (
          <Text size="1" as="p" style={{ color: 'var(--app-hint)', margin: '2px 0 0', opacity: 0.75 }}>
            {hint}
          </Text>
        )}
      </Box>
      <Text
        size="2"
        weight="medium"
        style={{
          flex: '0 0 auto',
          textAlign: 'right',
          color: accent ? `var(--${accent}-11)` : undefined,
        }}
      >
        {value}
      </Text>
    </Flex>
  );
}

function NetRow({ link }: { link: NetLink }) {
  const icon =
    link.kind === 'wifi' ? <Wifi size={17} /> : link.kind === 'vpn' ? <Lock size={17} /> : <Cable size={17} />;

  // The name people actually recognise: the SSID for Wi-Fi, otherwise whatever
  // macOS calls the service, falling back to the raw interface.
  const title = link.ssid ?? link.service ?? link.iface;

  const detail: string[] = [];
  if (link.ip) detail.push(link.ip);
  if (link.kind === 'wifi' && link.channel) detail.push(link.channel);
  if (link.kind === 'wifi' && link.security) detail.push(link.security);
  if (link.kind !== 'wifi' && link.service && link.ssid) detail.push(link.service);

  const sig = link.signalDbm != null ? signalLabel(link.signalDbm) : null;

  return (
    <Flex align="start" gap="3" style={{ padding: '10px 12px' }}>
      <Box style={{ color: 'var(--app-hint)', flex: '0 0 auto', paddingTop: 1 }}>{icon}</Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap="2" wrap="wrap">
          <Text size="2" weight="medium" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </Text>
          {link.primary && (
            <Badge size="1" color="jade" variant="soft">
              весь трафик
            </Badge>
          )}
        </Flex>
        {detail.length > 0 && (
          <Text size="1" as="p" style={{ color: 'var(--app-hint)', margin: '2px 0 0' }}>
            {detail.join(' · ')}
          </Text>
        )}
      </Box>
      {sig && link.signalDbm != null && (
        <Box style={{ flex: '0 0 auto', textAlign: 'right' }}>
          <Text size="2" weight="medium" style={{ color: `var(--${sig.color}-11)` }}>
            {link.signalDbm} дБм
          </Text>
          <Text size="1" as="p" style={{ color: 'var(--app-hint)', margin: 0 }}>
            {sig.text}
          </Text>
        </Box>
      )}
    </Flex>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Text size="1" weight="bold" style={{ color: 'var(--app-hint)', padding: '0 12px', letterSpacing: 0.4 }}>
        {title.toUpperCase()}
      </Text>
      <Box
        style={{
          marginTop: 6,
          borderRadius: 12,
          background: 'var(--app-secondary-bg)',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * The hardware half of the panel. Split out so a Mac that has stopped answering can
 * blank these rows without also blanking the Codex limits above them — those two
 * readings fail independently and one is often current while the other is not.
 */
function Hardware({ metrics }: { metrics: MacMetrics }) {
  const { battery, memory, disk, net } = metrics;
  const [one, five, fifteen] = metrics.load;

  // Load is per-core: 8.0 on 8 cores means fully committed, not "eight times over".
  const loadRatio = one / Math.max(1, metrics.cores);
  const loadAccent = loadRatio >= 1 ? 'red' : loadRatio >= 0.7 ? 'amber' : undefined;

  const swapUsed = memory.swapUsedBytes;
  // Swap in active use is the honest pressure signal on macOS, where "free memory"
  // is a number the OS deliberately keeps small.
  const swapAccent = swapUsed != null && swapUsed > 2 * GB ? 'amber' : undefined;

  const batteryLow = battery?.percent != null && battery.percent <= 20 && battery.source !== 'ac';

  return (
    <>
      <Section title="Железо">
          {battery && (
            <Row
              icon={battery.source === 'ac' ? <BatteryCharging size={17} /> : <Battery size={17} />}
              label="Батарея"
              hint={
                [
                  battery.source === 'ac' ? 'от сети' : 'от батареи',
                  battery.state ? (BATTERY_STATE[battery.state] ?? battery.state) : null,
                  battery.minutesLeft != null
                    ? `${minutes(battery.minutesLeft)} ${battery.source === 'ac' ? 'до полной' : 'осталось'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              value={battery.percent != null ? `${battery.percent}%` : '—'}
              accent={batteryLow ? 'red' : undefined}
            />
          )}
          <Row
            icon={<Sun size={17} />}
            label="Яркость экрана"
            // Nits in the hint, because the percentage is a share of the panel's nit
            // ceiling and not the slider position — the raw number is what makes that
            // checkable instead of something you have to believe.
            hint={metrics.brightness ? `${metrics.brightness.nits} из ${metrics.brightness.maxNits} нит` : undefined}
            value={metrics.brightness ? `${metrics.brightness.percent}%` : '—'}
          />
          <Row
            icon={<Cpu size={17} />}
            label="Нагрузка"
            hint={`${five.toFixed(1)} за 5 мин · ${fifteen.toFixed(1)} за 15 мин · ${metrics.cores} ядер`}
            value={one.toFixed(1)}
            accent={loadAccent}
          />
          <Row
            icon={<MemoryStick size={17} />}
            label="Память"
            hint={
              swapUsed != null && memory.swapTotalBytes != null
                ? `swap ${gb(swapUsed)} из ${gb(memory.swapTotalBytes)}`
                : undefined
            }
            value={
              memory.freePercent != null ? `${memory.freePercent}% свободно` : gb(memory.totalBytes)
            }
            accent={swapAccent}
          />
          {disk && (
            <Row
              icon={<HardDrive size={17} />}
              label="Диск"
              hint={`из ${gb(disk.totalBytes)}`}
              value={`${gb(disk.freeBytes)} свободно`}
            />
          )}
        <Row icon={<Clock size={17} />} label="Аптайм" value={duration(metrics.uptimeSec)} />
      </Section>

      <Section title="Сети">
        {net.length === 0 ? (
          <Box style={{ padding: '12px' }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              Ни одного активного интерфейса
            </Text>
          </Box>
        ) : (
          net.map((link) => <NetRow key={link.iface} link={link} />)
        )}
      </Section>

      <Text size="1" align="center" style={{ color: 'var(--app-hint)' }}>
        {new Date(metrics.at).toLocaleTimeString('ru-RU')}
      </Text>
    </>
  );
}

export function Mac({
  metrics,
  usage,
  onRefreshUsage,
}: {
  metrics: MacMetrics | null;
  usage: CodexUsage | null;
  onRefreshUsage: () => void;
}) {
  return (
    <ScrollArea style={{ height: '100%' }}>
      <Flex direction="column" gap="4" p="3">
        {/* First, because it is the one number here that changes what he does next. */}
        <CodexLimits usage={usage} onRefresh={onRefreshUsage} />

        {metrics ? (
          <Hardware metrics={metrics} />
        ) : (
          <Text size="2" align="center" style={{ color: 'var(--app-hint)', padding: '12px 0' }}>
            Мак не отвечает — он спит или потерял сеть. Показывать последние известные
            цифры как текущие было бы враньём, поэтому здесь пусто.
          </Text>
        )}
      </Flex>
    </ScrollArea>
  );
}
